import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { authApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { Field } from '@/features/auth'
import { useT, useTx } from '@/i18n'

export function ForgotPasswordPage() {
  const t = useT()
  const tx = useTx()
  const [email, setEmail] = useState('')
  const forgot = useMutation({ mutationFn: () => authApi.forgotPassword(email.trim()) })
  const error = forgot.error instanceof ApiError ? forgot.error : null

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    forgot.mutate()
  }

  if (forgot.isSuccess) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-extrabold">{t('password.sentTitle')}</h1>
        {/* 这里刻意不说「该邮箱存在」：说了就等于提供了账号探测器 */}
        <p className="text-[15px] text-ink-muted">
          {tx('password.sentBody', { email: <span className="font-bold">{email}</span> })}
        </p>
        <Link to="/login" className="inline-block text-brand-ink hover:underline">
          {t('password.backToLogin')}
        </Link>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <h1 className="text-3xl font-extrabold">{t('password.forgotTitle')}</h1>
      <p className="text-[15px] text-ink-muted">{t('password.forgotBody')}</p>

      <Field
        label={t('auth.email')}
        name="email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />

      {error && (
        <p role="alert" className="text-sm text-danger">
          {error.message}
        </p>
      )}

      <Button type="submit" size="lg" fullWidth loading={forgot.isPending}>
        {t('password.sendLink')}
      </Button>
      <p className="text-center text-[15px]">
        <Link to="/login" className="text-brand-ink hover:underline">
          {t('password.backToLogin')}
        </Link>
      </p>
    </form>
  )
}

export function ResetPasswordPage() {
  const t = useT()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [password, setPassword] = useState('')
  const navigate = useNavigate()

  const reset = useMutation({
    mutationFn: () => authApi.resetPassword(token, password),
    onSuccess: () => navigate('/login', { replace: true }),
  })
  const error = reset.error instanceof ApiError ? reset.error : null

  if (!token) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-extrabold">{t('password.incomplete')}</h1>
        <p className="text-[15px] text-ink-muted">{t('password.incompleteBody')}</p>
        <Link to="/forgot-password" className="inline-block text-brand-ink hover:underline">
          {t('password.requestAgain')}
        </Link>
      </div>
    )
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        reset.mutate()
      }}
      className="space-y-4"
      noValidate
    >
      <h1 className="text-3xl font-extrabold">{t('password.newTitle')}</h1>
      <p className="text-[15px] text-ink-muted">{t('password.newBody')}</p>

      <Field
        label={t('password.newLabel')}
        name="password"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={error?.fieldError('password')}
        required
      />

      {error && !error.fieldError('password') && (
        <p role="alert" className="text-sm text-danger">
          {error.message}
        </p>
      )}

      <Button type="submit" size="lg" fullWidth loading={reset.isPending}>
        {t('password.newTitle')}
      </Button>
    </form>
  )
}
